/**
 * 교사 상세: 기본정보 · 교육 이수 · BT 4단계 · 수료증
 */
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Drawer, IconButton, MenuItem, Tab, Table, TableBody,
  TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { api, CertData } from '../api';
import { toEnName, C, CLUBS, certDate, clubLabel, formatPhoneInput, ymd, ymdhm, VERSES } from '../common';
import { CertificateView } from '../certificate/Certificate';
import { CertDateDialog, CertRevokeDialog } from './certDialogs';
import { StatusChip, ToneChip } from './ui';

type Detail = {
  teacher: any;
  personKey: string;
  registrations: Array<{ _id: string; keyCode: string; eventId: string; churchName: string; status: string; createdAt: string }>;
  courses: Array<{ _id: string; code: string; name: string; category: string; status: string; mode: string; prerequisites: string[] }>;
  records: Array<{ _id: string; courseCode: string; year: number; result: string; source: string; sessionTitle: string; note?: string; completedAt?: string }>;
  certificates: Array<CertData & { revokeReason?: string; dateChanges: any[]; receipts: any[]; adminPrints: number }>;
  reports: Array<{ _id: string; status: any; club: string; submittedAt: string; decidedAt?: string; checklist: any; archivePath?: string; rejectReason?: string; supersededBy?: string; fileCount: number }>;
  attendance: Array<{ at: string; sessionId?: string; location?: string; method?: string }>;
};

const RESULT_LABEL: Record<string, string> = { completed: '수료', attended: '출석', in_progress: '진행 중' };

const TeacherDrawer: React.FC<{ teacherId: string | null; onClose: () => void; onChanged?: () => void }> = ({ teacherId, onClose, onChanged }) => {
  const [tab, setTab] = useState(0);
  const [d, setD] = useState<Detail | null>(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState<any>({});
  const [saved, setSaved] = useState('');
  const [dateCert, setDateCert] = useState<any>(null);
  const [revokeCert, setRevokeCert] = useState<any>(null);
  const [addRec, setAddRec] = useState({ courseId: '', year: String(new Date().getFullYear()) });

  const load = () => {
    if (!teacherId) return;
    setError('');
    api<{ data: Detail }>(`/admin/edu/teachers/${teacherId}`).then((r) => {
      setD(r.data);
      const t = r.data.teacher;
      setForm({ teacherNameEn: t.teacherNameEn || '', clubs: t.clubs || [], position: t.position || '', awanaRole: t.awanaRole || '', teacherPhone: t.teacherPhone || '' });
    }).catch((e) => setError(e.message));
  };
  useEffect(() => { setD(null); setTab(0); setSaved(''); load(); }, [teacherId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaved(''); setError('');
    try {
      await api(`/admin/edu/teachers/${teacherId}`, { method: 'PUT', body: form });
      setSaved('저장했습니다.'); load(); onChanged?.();
    } catch (e: any) { setError(e.message); }
  };
  const addRecord = async () => {
    try {
      await api(`/admin/edu/teachers/${teacherId}/records`, { body: { courseId: addRec.courseId, year: Number(addRec.year) } });
      load(); onChanged?.();
    } catch (e: any) { setError(e.message); }
  };
  const removeRecord = async (id: string) => {
    if (!window.confirm('이 이수 기록을 지울까요?')) return;
    try { await api(`/admin/edu/records/${id}`, { method: 'DELETE' }); load(); onChanged?.(); } catch (e: any) { setError(e.message); }
  };

  const t = d?.teacher;
  const latestReport = d?.reports.find((r) => !r.supersededBy && r.status === 'approved') || d?.reports.find((r) => !r.supersededBy);
  const done = new Set(d?.records.filter((r) => r.result === 'completed').map((r) => r.courseCode));
  const partial = new Set(d?.records.filter((r) => r.result !== 'completed').map((r) => r.courseCode));
  const categories = Array.from(new Set(d?.courses.filter((c) => c.status === 'active').map((c) => c.category) || []));
  const years = Array.from(new Set(d?.records.map((r) => r.year) || [])).sort((a, b) => b - a);

  return (
    <Drawer anchor="right" open={Boolean(teacherId)} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 640 }, bgcolor: '#fffdf6' } }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${C.line}`, bgcolor: '#fff' }}>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={20} fontWeight={800}>{t?.teacherName || '…'} <Typography component="span" sx={{ fontSize: 13, color: C.sub, fontWeight: 600 }}>{t?.teacherNameEn}</Typography></Typography>
          <Typography fontSize={13} color={C.sub}>{t?.churchName} {t?.regNo && `· 등록번호 ${t.regNo}`} {t?.keyCode && `· ${t.keyCode}`}</Typography>
        </Box>
        <IconButton onClick={onClose}><Close /></IconButton>
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ px: 1, bgcolor: '#fff', borderBottom: `1px solid ${C.line}` }}>
        <Tab label="기본정보" /><Tab label="교육 이수" /><Tab label="BT 4단계" /><Tab label={`수료증${d?.certificates.length ? ` ${d.certificates.filter((c) => c.status === 'issued').length}` : ''}`} />
      </Tabs>
      <Box sx={{ p: 2, overflowY: 'auto' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!d ? <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box> : (
          <>
            {tab === 0 && (
              <Box sx={{ display: 'grid', gap: 2 }}>
                {saved && <Alert severity="success">{saved}</Alert>}
                <TextField label="영문 이름 (수료증 영문 표기)" value={form.teacherNameEn} onChange={(e) => setForm({ ...form, teacherNameEn: toEnName(e.target.value) })} placeholder="HONG GILDONG" />
                <TextField select label="담당 클럽 (여러 개 선택)" SelectProps={{ multiple: true, renderValue: (v: any) => (v as string[]).join(' · ') }}
                  value={form.clubs || []} onChange={(e) => setForm({ ...form, clubs: e.target.value })}>
                  {CLUBS.map((c) => <MenuItem key={c.code} value={c.code}><Checkbox size="small" checked={(form.clubs || []).includes(c.code)} />{c.label}</MenuItem>)}
                </TextField>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <TextField label="직분" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
                  <TextField label="어와나 역할" value={form.awanaRole} onChange={(e) => setForm({ ...form, awanaRole: e.target.value })} />
                </Box>
                <TextField label="휴대폰" value={form.teacherPhone} onChange={(e) => setForm({ ...form, teacherPhone: formatPhoneInput(e.target.value) })} />
                <Box><Button variant="contained" onClick={save}>저장</Button></Box>
                <Typography fontWeight={800} mt={1}>등록 이력</Typography>
                <Table size="small"><TableHead><TableRow><TableCell>등록</TableCell><TableCell>교사번호</TableCell><TableCell>행사</TableCell><TableCell>상태</TableCell></TableRow></TableHead>
                  <TableBody>{d.registrations.map((r) => (
                    <TableRow key={r._id}><TableCell>{ymd(r.createdAt)}</TableCell><TableCell sx={{ fontFamily: 'monospace' }}>{r.keyCode}</TableCell><TableCell>{r.eventId}</TableCell><TableCell>{r.status}</TableCell></TableRow>
                  ))}</TableBody></Table>
                {d.registrations.length > 1 && <Alert severity="warning">같은 이름+휴대폰으로 {d.registrations.length}건 등록되어 있어 1명으로 묶어 보여줍니다.</Alert>}
                <Typography fontWeight={800} mt={1}>최근 출석</Typography>
                {d.attendance.length ? d.attendance.slice(0, 8).map((a, i) => (
                  <Typography key={i} fontSize={13}>{ymdhm(a.at)} · {a.location || a.sessionId || '강의'} {a.method && <span style={{ color: C.sub }}>({a.method})</span>}</Typography>
                )) : <Typography fontSize={13} color={C.sub}>출석 기록 없음</Typography>}
              </Box>
            )}

            {tab === 1 && (
              <Box sx={{ display: 'grid', gap: 2 }}>
                {categories.map((cat) => (
                  <Box key={cat}>
                    <Typography fontSize={13} fontWeight={800} color={C.sub} mb={0.75}>{cat === 'CC' ? 'CC · Command College (조정관 학교)' : cat}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {d.courses.filter((c) => c.category === cat && c.status === 'active').map((c) => {
                        const state = done.has(c.code) ? 'done' : partial.has(c.code) ? 'part' : 'none';
                        return (
                          <Box key={c.code} sx={{ px: 1.25, py: 0.75, borderRadius: 2, border: `1px solid ${state === 'done' ? '#f0cf5a' : C.line}`, bgcolor: state === 'done' ? '#fff6cc' : '#fff', minWidth: 120 }}>
                            <Typography fontSize={13} fontWeight={800}>{state === 'done' ? '✔' : state === 'part' ? '◐' : '○'} {c.code}</Typography>
                            <Typography fontSize={11.5} color={C.sub}>{c.name}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
                <Typography fontWeight={800} mt={1}>연도별 이력</Typography>
                {years.length ? years.map((y) => (
                  <Box key={y}>
                    <Typography fontSize={13} fontWeight={800} sx={{ bgcolor: '#fff3cf', px: 1, py: 0.25, borderRadius: 1, display: 'inline-block', mb: 0.5 }}>{y}년</Typography>
                    {d.records.filter((r) => r.year === y).map((r) => (
                      <Box key={r._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: `1px dashed ${C.line}` }}>
                        <Typography fontSize={13.5} fontWeight={700} sx={{ minWidth: 70 }}>{r.courseCode}</Typography>
                        <Typography fontSize={13} sx={{ flex: 1 }}>{r.sessionTitle || r.note || d.courses.find((c) => c.code === r.courseCode)?.name}</Typography>
                        <ToneChip tone={r.result === 'completed' ? 'ok' : 'info'} label={RESULT_LABEL[r.result] || r.result} />
                        {r.source !== 'report' && <Button size="small" onClick={() => removeRecord(r._id)}>삭제</Button>}
                      </Box>
                    ))}
                  </Box>
                )) : <Typography fontSize={13} color={C.sub}>이수 기록이 없습니다.</Typography>}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, p: 1.5, bgcolor: '#fff', border: `1px dashed ${C.line}`, borderRadius: 2 }}>
                  <TextField select size="small" label="과정" value={addRec.courseId} onChange={(e) => setAddRec({ ...addRec, courseId: e.target.value })} sx={{ flex: 1 }}>
                    {d.courses.filter((c) => c.code !== 'BT' && c.status === 'active').map((c) => <MenuItem key={c._id} value={c._id}>{c.code} · {c.name}</MenuItem>)}
                  </TextField>
                  <TextField size="small" label="연도" value={addRec.year} onChange={(e) => setAddRec({ ...addRec, year: e.target.value.replace(/\D/g, '').slice(0, 4) })} sx={{ width: 90 }} />
                  <Button variant="outlined" disabled={!addRec.courseId} onClick={addRecord}>이수 추가</Button>
                </Box>
              </Box>
            )}

            {tab === 2 && (
              latestReport ? (
                <Box sx={{ display: 'grid', gap: 1.25 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <StatusChip status={latestReport.status} /> <Typography fontSize={13}>{clubLabel(latestReport.club)} · 제출 {ymd(latestReport.submittedAt)} {latestReport.decidedAt && `· 처리 ${ymd(latestReport.decidedAt)}`}</Typography>
                  </Box>
                  {latestReport.rejectReason && latestReport.status === 'rejected' && <Alert severity="error">반려 사유: {latestReport.rejectReason}</Alert>}
                  {(() => {
                    const c = latestReport.checklist || {};
                    const verses: boolean[] = c.step3Verses || [];
                    return (
                      <>
                        <Row label="1단계 BT 참석" ok={c.step1Attended} text={c.step1Auto ? '출석 기록으로 자동 확인' : c.step1Attended ? '확인' : '미확인'} />
                        <Row label="2단계 점수" ok={Number(c.step2Score) >= 80} text={c.step2Score === null || c.step2Score === undefined ? '미입력' : `${c.step2Score}점`} />
                        <Row label="3단계 암송" ok={verses.filter(Boolean).length === 10} text={`${verses.filter(Boolean).length} / 10`} />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 2 }}>
                          {VERSES.map((v, i) => <Chip key={v} size="small" label={v} sx={{ height: 20, fontSize: 11, bgcolor: verses[i] ? '#fff3cf' : C.grayBg, color: verses[i] ? '#6b5200' : C.gray }} />)}
                        </Box>
                        <Row label="4단계 견학/실습" ok={(c.step4Visit || c.step4Practice) && c.step4Signed}
                          text={[c.step4Visit && `견학${c.step4VisitChurch ? `(${c.step4VisitChurch})` : ''}`, c.step4Practice && '실습', c.step4Signed && '서명'].filter(Boolean).join(' · ') || '미확인'} />
                        <Row label="최종 서명" ok={c.signTeacher && c.signCoordinator} text={[c.signTeacher && '본인', c.signCoordinator && '조정관/감독관'].filter(Boolean).join(' · ') || '미확인'} />
                        {latestReport.archivePath && <Typography fontSize={12.5} color={C.sub} mt={1}>📁 보관: bt-reports/{latestReport.archivePath}</Typography>}
                      </>
                    );
                  })()}
                </Box>
              ) : <Typography color={C.sub}>제출된 완성보고서가 없습니다.</Typography>
            )}

            {tab === 3 && (
              d.certificates.length ? (
                <Box sx={{ display: 'grid', gap: 3 }}>
                  {d.certificates.map((c) => {
                    const church = c.receipts?.filter((r: any) => r.by === 'church') || [];
                    return (
                      <Box key={c._id} sx={{ opacity: c.status === 'revoked' ? 0.55 : 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          <Typography fontWeight={800} sx={{ fontFamily: 'monospace' }}>{c.serial}</Typography>
                          <Chip size="small" label={c.clubLabel} />
                          {c.status === 'revoked' ? <ToneChip tone="err" label={`취소됨 · ${c.revokeReason || ''}`} />
                            : church.length ? <ToneChip tone="ok" label={`교회 수령 ${ymdhm(church[church.length - 1].at)}`} /> : <ToneChip tone="gray" dashed label="미수령" />}
                        </Box>
                        <Box sx={{ border: `1px solid ${C.line}`, borderRadius: 1, overflow: 'hidden' }}><CertificateView cert={c} fluid /></Box>
                        <Typography fontSize={12.5} color={C.sub} mt={0.75}>수료일 {certDate(c.completedAt)} · 발급 {ymd(c.issuedAt)} · 관리자 인쇄 {c.adminPrints || 0}회</Typography>
                        {c.dateChanges?.map((h: any, i: number) => <Typography key={i} fontSize={12} color={C.warn}>날짜 변경 {ymd(h.from)} → {ymd(h.to)} ({h.reason}) · {ymdhm(h.at)}</Typography>)}
                        {c.status === 'issued' && (
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button variant="contained" size="small" onClick={() => window.open(`/admin/bt-cert/print?ids=${c._id}`, '_blank')}>인쇄 / PDF</Button>
                            <Button variant="outlined" size="small" onClick={() => setDateCert(c)}>수료일 변경</Button>
                            <Button size="small" color="error" onClick={() => setRevokeCert(c)}>취소</Button>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : <Typography color={C.sub}>발급된 수료증이 없습니다.</Typography>
            )}
          </>
        )}
      </Box>
      <CertDateDialog cert={dateCert} onClose={() => setDateCert(null)} onDone={load} />
      <CertRevokeDialog cert={revokeCert} onClose={() => setRevokeCert(null)} onDone={() => { load(); onChanged?.(); }} />
    </Drawer>
  );
};

const Row: React.FC<{ label: string; ok: any; text: string }> = ({ label, ok, text }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.5, borderBottom: `1px dashed ${C.line}` }}>
    <Typography sx={{ width: 22, color: ok ? C.ok : C.warn, fontWeight: 800 }}>{ok ? '✔' : '!'}</Typography>
    <Typography fontSize={14} fontWeight={700} sx={{ width: 130 }}>{label}</Typography>
    <Typography fontSize={13.5}>{text}</Typography>
  </Box>
);

export default TeacherDrawer;
